import React, { useEffect, useRef, useState } from "react";
import {
    FiPlus, FiEdit2, FiTrash2, FiMail, FiPhone, FiCreditCard,
    FiSearch, FiX, FiClock, FiLogIn, FiLogOut, FiShield,
    FiPackage, FiLink, FiUsers, FiTrendingUp, FiUser, FiBriefcase,
} from "react-icons/fi";
import axiosInstance from "../utils/api";
import toast from "react-hot-toast";
import ContractorModal from "./ContractorModal";
import DeleteModal from "./deleteModal";
import { useNavigate } from "react-router-dom";
import { io } from "socket.io-client";

const ContractorsTab = ({ eventId, isSubAdmin }) => {
    const navigate = useNavigate();
    const socketRef = useRef(null);

    const [contractors, setContractors] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selected, setSelected] = useState(null);
    const [open, setOpen] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [deleting, setDeleting] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");
    const [detailsModal, setDetailsModal] = useState(null);
    const [loadingBadges, setLoadingBadges] = useState(false);
    const [newBadgesMap, setNewBadgesMap] = useState({});

    /* ── fetch ── */
    const fetchInitialData = async () => {
        try {
            setLoading(true);

            const { data } = await axiosInstance.get(`/auth/contractors/${eventId}`);
            const contractorsData = data.contractors || [];
            setContractors(contractorsData);

            const badgeResults = await Promise.all(
                contractorsData.map((c) =>
                    axiosInstance
                        .get(`/badge/by-standiste/${c._id}`)
                        .then(({ data }) => {
                            const badges = Array.isArray(data) ? data : data.badges || [];
                            return { id: c._id, hasNew: badges.some((b) => b.new === true) };
                        })
                        .catch(() => ({ id: c._id, hasNew: false }))
                )
            );

            const map = {};
            badgeResults.forEach(({ id, hasNew }) => { map[id] = hasNew; });
            setNewBadgesMap(map);
        } catch {
            toast.error("Failed to load contractors");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchInitialData(); }, [eventId]);

    /* ── socket ── */
    useEffect(() => {
        const socket = io("https://api.expo-pilot.com");
        socketRef.current = socket;

        socket.on("badge-scanned", async (data) => {
            setDetailsModal((prev) => {
                if (!prev || prev._id !== data.chefId) return prev;
                setLoadingBadges(true);
                axiosInstance
                    .get(`/badge/by-chef/${data.chefId}`)
                    .then(({ data: res }) => {
                        const badgesArray = Array.isArray(res) ? res : res.badges || [];
                        setDetailsModal((current) => {
                            if (!current || current._id !== data.chefId) return current;
                            return { ...current, badges: badgesArray };
                        });
                    })
                    .catch(() => toast.error("Failed to refresh — showing last known data"))
                    .finally(() => setLoadingBadges(false));
                return { ...prev };
            });
        });

        return () => socket.disconnect();
    }, []);

    const handleDeleteConfirm = async () => {
        if (!deleteTarget) return;
        try {
            setDeleting(true);
            await axiosInstance.delete(`/auth/user/${deleteTarget._id}`);
            toast.success("Contractor deleted");
            setDeleteTarget(null);
            fetchInitialData();
        } catch {
            toast.error("Failed to delete contractor");
        } finally {
            setDeleting(false);
        }
    };

    /* ── copy link ── */
    const handleCopyContractorLink = () => {
        navigator.clipboard.writeText(`${window.location.origin}/${eventId}/contractor`);
        toast.success("Contractor registration link copied!");
    };

    const handleCardClick = async (contractor) => {
        try {
            setLoadingBadges(true);
            setDetailsModal({ ...contractor, badges: [] });

            if (socketRef.current?.connected) {
                socketRef.current.emit("join-chef-room", contractor._id);
            }

            const { data: standisteData } = await axiosInstance.get(`/badge/by-standiste/${contractor._id}`);
            const directBadges = Array.isArray(standisteData) ? standisteData : standisteData.badges || [];
            const newBadges = directBadges.filter((b) => b.new === true);
            if (newBadges.length > 0) {
                await Promise.all(newBadges.map((b) => axiosInstance.put(`/badge/${b._id}`, { new: false })));
                setNewBadgesMap((prev) => ({ ...prev, [contractor._id]: false }));
            }

            const { data: allData } = await axiosInstance.get(`/badge/by-chef/${contractor._id}`);
            const badgesArray = Array.isArray(allData) ? allData : allData.badges || [];

            const newBadgeIds = new Set(newBadges.map((b) => b._id));
            badgesArray.forEach((b) => { if (newBadgeIds.has(b._id)) b.new = false; });

            setDetailsModal({ ...contractor, badges: badgesArray });
        } catch {
            toast.error("Failed to load badge information");
            setDetailsModal({ ...contractor, badges: [] });
        } finally {
            setLoadingBadges(false);
        }
    };

    const formatDate = (d) =>
        new Date(d).toLocaleString("en-US", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });

    const getAllScanLogs = (badges) => {
        if (!Array.isArray(badges)) return [];
        return badges
            .flatMap((badge) =>
                (badge.scanLogs || []).map((log) => ({
                    ...log,
                    holderName: badge.name || badge.standiste?.name || badge.chef?.name || "—",
                    holderCin: badge.cin || null,
                }))
            )
            .sort((a, b) => new Date(b.scannedAt) - new Date(a.scannedAt));
    };

    const getOnsiteCount = (badges) => {
        if (!Array.isArray(badges)) return 0;
        return badges.filter((badge) => {
            if (!badge.scanLogs?.length) return false;
            const latest = [...badge.scanLogs].sort((a, b) => new Date(b.scannedAt) - new Date(a.scannedAt))[0];
            return latest.action === "entry";
        }).length;
    };

    const filteredContractors = contractors.filter((c) => {
        const q = searchQuery.toLowerCase();
        return (
            c.name?.toLowerCase().includes(q) ||
            c.email?.toLowerCase().includes(q) ||
            c.cin?.toLowerCase().includes(q)
        );
    });

    const ProfileAvatar = ({ user, size = "md" }) => {
        const sz = { sm: "w-10 h-10 text-xs", md: "w-12 h-12 text-sm", lg: "w-14 h-14 text-base" };
        return (
            <div className={`${sz[size]} rounded-full bg-gradient-to-br from-red-800 to-red-900 flex items-center justify-center border-2 border-white shadow-sm ring-2 ring-slate-100 flex-shrink-0`}>
                <span className="font-bold text-white">{user.name?.charAt(0)?.toUpperCase() || "?"}</span>
            </div>
        );
    };

    const BadgesPanel = ({ badges, isLoading }) => {
        const logs = getAllScanLogs(badges);
        const total = Array.isArray(badges) ? badges.length : 0;
        const onsite = getOnsiteCount(badges);
        const offsite = total - onsite;
        const entries = logs.filter((l) => l.action === "entry").length;
        const exits = logs.filter((l) => l.action === "exit").length;

        if (isLoading) {
            return (
                <div className="flex items-center justify-center py-16">
                    <div className="text-center">
                        <div className="w-6 h-6 border-2 border-red-700 border-t-transparent rounded-full animate-spin mx-auto mb-2" />
                        <span className="text-xs text-slate-400">Loading badge data...</span>
                    </div>
                </div>
            );
        }

        return (
            <div className="space-y-4">
                {/* stats */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                    <div className="rounded-lg p-3 bg-amber-50 border border-amber-200">
                        <div className="flex items-center gap-1.5 mb-1">
                            <FiPackage className="text-amber-600" size={11} />
                            <span className="text-[10px] font-semibold text-amber-700">Total Badges</span>
                        </div>
                        <div className="text-2xl font-bold text-slate-800">{total}</div>
                    </div>
                    <div className="rounded-lg p-3 bg-green-50 border border-green-200">
                        <div className="flex items-center gap-1.5 mb-1">
                            <FiLogIn className="text-green-600" size={11} />
                            <span className="text-[10px] font-semibold text-green-700">Onsite</span>
                        </div>
                        <div className="text-2xl font-bold text-slate-800">{onsite}</div>
                    </div>
                    <div className="rounded-lg p-3 bg-slate-50 border border-slate-200">
                        <div className="flex items-center gap-1.5 mb-1">
                            <FiLogOut className="text-slate-500" size={11} />
                            <span className="text-[10px] font-semibold text-slate-600">Offsite</span>
                        </div>
                        <div className="text-2xl font-bold text-slate-800">{offsite}</div>
                    </div>
                    <div className="rounded-lg p-3 bg-orange-50 border border-orange-200">
                        <div className="flex items-center gap-1.5 mb-1">
                            <FiTrendingUp className="text-orange-500" size={11} />
                            <span className="text-[10px] font-semibold text-orange-600">Total Scans</span>
                        </div>
                        <div className="text-2xl font-bold text-slate-800">{logs.length}</div>
                    </div>
                </div>

                {logs.length > 0 && (
                    <div className="flex gap-2">
                        <div className="flex-1 flex items-center gap-2 bg-green-50 border border-green-200 rounded-lg px-3 py-2">
                            <FiLogIn className="text-green-600 flex-shrink-0" size={13} />
                            <span className="text-xs font-medium text-green-700">{entries} Entries</span>
                        </div>
                        <div className="flex-1 flex items-center gap-2 bg-orange-50 border border-orange-200 rounded-lg px-3 py-2">
                            <FiLogOut className="text-orange-500 flex-shrink-0" size={13} />
                            <span className="text-xs font-medium text-orange-600">{exits} Exits</span>
                        </div>
                    </div>
                )}

                <div>
                    <h4 className="text-xs font-semibold text-slate-600 mb-2 flex items-center gap-1.5">
                        <FiClock size={12} /> All Scan Activity
                    </h4>
                    {logs.length === 0 ? (
                        <div className="text-center py-10 bg-slate-50 rounded-lg border border-dashed border-slate-200">
                            <FiClock size={22} className="mx-auto mb-2 text-slate-300" />
                            <p className="text-xs text-slate-400">No scans recorded for this contractor</p>
                        </div>
                    ) : (
                        <div className="space-y-1.5 max-h-64 overflow-y-auto pr-0.5">
                            {logs.map((log, idx) => (
                                <div
                                    key={idx}
                                    className={`flex items-center justify-between p-2.5 rounded-md border-l-4 ${
                                        log.action === "entry"
                                            ? "bg-green-50 border-green-500"
                                            : "bg-orange-50 border-orange-400"
                                    }`}
                                >
                                    <div className="flex items-center gap-2 min-w-0">
                                        {log.action === "entry"
                                            ? <FiLogIn className="text-green-600 flex-shrink-0" size={14} />
                                            : <FiLogOut className="text-orange-500 flex-shrink-0" size={14} />
                                        }
                                        <div className="min-w-0">
                                            <div className="flex items-center gap-1.5 flex-wrap">
                                                <span className="text-xs font-semibold text-slate-800 capitalize">{log.action}</span>
                                                <span className="text-[10px] px-1.5 py-0.5 rounded-full font-medium bg-amber-100 text-amber-700">
                                                    contractor
                                                </span>
                                            </div>
                                            <div className="flex items-center gap-1 mt-0.5">
                                                <FiUser size={10} className="text-slate-400 flex-shrink-0" />
                                                <span className="text-[11px] text-slate-600 truncate">{log.holderName}</span>
                                                {log.holderCin && (
                                                    <span className="text-[10px] text-slate-400 flex-shrink-0">· {log.holderCin}</span>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="text-right flex-shrink-0 ml-3">
                                        <div className="flex items-center justify-end gap-1 text-xs text-slate-700 font-medium">
                                            <FiShield size={10} className="text-slate-400" />
                                            {log.gate}
                                        </div>
                                        <div className="text-[10px] text-slate-400 mt-0.5">{formatDate(log.scannedAt)}</div>
                                        {log.agent?.name && (
                                            <div className="text-[10px] text-slate-400">by {log.agent.name}</div>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        );
    };

    /* ════════════════════════════ render ════════════════════════════ */

    if (loading) return <div className="py-6 text-center text-xs text-slate-500">Loading...</div>;

    return (
        <div className="space-y-3">

            {/* header row */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2">
                <h2 className="text-lg font-bold text-slate-800">Contractors</h2>
                <div className="flex items-center gap-2 flex-wrap">
                    <button
                        onClick={handleCopyContractorLink}
                        className="flex items-center gap-1.5 px-3 py-1.5 bg-slate-700 hover:bg-slate-800 text-white rounded-md text-xs font-medium transition shadow-sm"
                    >
                        <FiLink size={13} /> Copy Registration Link
                    </button>
                </div>
            </div>

            <div className="relative">
                <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={14} />
                <input
                    type="text"
                    placeholder="Search by name, email, or CIN..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-9 pr-8 py-2 text-xs border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-800 focus:border-transparent"
                />
                {searchQuery && (
                    <button onClick={() => setSearchQuery("")} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                        <FiX size={14} />
                    </button>
                )}
            </div>

            {!filteredContractors.length ? (
                <div className="text-center py-10 border-2 border-dashed border-slate-200 rounded-lg text-xs text-slate-500">
                    {searchQuery ? "No contractors match your search" : "No contractors yet"}
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
                    {filteredContractors.map((c) => (
                        <div
                            key={c._id}
                            onClick={() => handleCardClick(c)}
                            className="relative bg-white border border-slate-200 rounded-lg p-3 hover:shadow-md hover:border-red-900 transition-all cursor-pointer group"
                        >
                            {newBadgesMap[c._id] && (
                                <div className="absolute top-2 right-2 flex items-center gap-1 bg-emerald-50 border border-emerald-300 text-emerald-700 text-[10px] font-semibold px-1.5 py-0.5 rounded-md">
                                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse flex-shrink-0" />
                                    New
                                </div>
                            )}

                            <div className="flex items-start gap-3 mb-2">
                                <ProfileAvatar user={c} size="sm" />
                                <div className="flex-1 min-w-0">
                                    <h3 className="font-semibold text-sm text-slate-800 group-hover:text-red-800 transition truncate">{c.name}</h3>
                                    <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-red-50 text-red-800 text-[10px] font-semibold rounded-full mt-1">
                                       Contractor
                                    </span>
                                </div>
                            </div>

                            <div className="space-y-1.5 text-xs text-slate-600 mb-2">
                                <div className="flex items-center gap-2">
                                    <FiMail className="text-slate-400 flex-shrink-0" size={12} />
                                    <span className="truncate">{c.email}</span>
                                </div>
                                {c.cin && (
                                    <div className="flex items-center gap-2">
                                        <FiCreditCard className="text-slate-400 flex-shrink-0" size={12} />
                                        <span className="truncate">{c.cin}</span>
                                    </div>
                                )}
                                {c.phone && (
                                    <div className="flex items-center gap-2">
                                        <FiPhone className="text-slate-400 flex-shrink-0" size={12} />
                                        <span className="truncate">{c.phone}</span>
                                    </div>
                                )}
                            </div>

                            {!isSubAdmin && <div className="flex gap-1.5 pt-2 border-t border-slate-100">
                                <button
                                    onClick={(e) => { e.stopPropagation(); setSelected(c); setOpen(true); }}
                                    className="flex-1 flex items-center justify-center gap-1 text-[11px] py-1.5 rounded-md text-slate-700 hover:bg-slate-100 font-medium transition"
                                >
                                    <FiEdit2 size={12} /> Edit
                                </button>
                                <button
                                    onClick={(e) => { e.stopPropagation(); setDeleteTarget(c); }}
                                    className="flex-1 flex items-center justify-center gap-1 text-[11px] py-1.5 rounded-md text-red-600 hover:bg-red-50 font-medium transition"
                                >
                                    <FiTrash2 size={12} /> Delete
                                </button>
                            </div>}
                        </div>
                    ))}
                </div>
            )}

            {detailsModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-xl max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col shadow-xl">

                        <div className="p-4 border-b border-slate-200 flex-shrink-0">
                            <div className="flex items-start justify-between gap-3 mb-3">
                                <div className="flex items-center gap-3 min-w-0 flex-1">
                                    <ProfileAvatar user={detailsModal} size="lg" />
                                    <div className="min-w-0">
                                        <h3 className="text-base font-bold text-slate-800 truncate">{detailsModal.name}</h3>
                                        <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-1 text-[11px] text-slate-500">
                                            <span className="flex items-center gap-1"><FiMail size={10} />{detailsModal.email}</span>
                                            {detailsModal.cin && <span className="flex items-center gap-1"><FiCreditCard size={10} />{detailsModal.cin}</span>}
                                            {detailsModal.phone && <span className="flex items-center gap-1"><FiPhone size={10} />{detailsModal.phone}</span>}
                                        </div>
                                        <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-red-50 text-red-800 text-[10px] font-semibold rounded-full mt-1.5">
                                             Contractor
                                        </span>
                                    </div>
                                </div>
                                <button onClick={() => setDetailsModal(null)} className="text-slate-400 hover:text-slate-600 flex-shrink-0">
                                    <FiX size={18} />
                                </button>
                            </div>

                            {!isSubAdmin && <div className="flex gap-2">
                                <button
                                    onClick={() => navigate(`/event/${eventId}/${detailsModal._id}`)}
                                    className="flex items-center gap-1.5 px-3 py-1.5 bg-slate-800 hover:bg-slate-700 text-white rounded-lg text-xs font-medium transition shadow-sm"
                                >
                                    <FiUsers size={13} /> View Staff
                                </button>
                            </div>}
                        </div>

                        <div className="flex-1 overflow-y-auto p-4">
                            <BadgesPanel badges={detailsModal.badges} isLoading={loadingBadges} />
                        </div>

                        <div className="p-4 border-t border-slate-200 flex justify-end gap-2 flex-shrink-0">
                            <button
                                onClick={() => setDetailsModal(null)}
                                className="px-3 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-md text-xs font-medium transition"
                            >
                                Close
                            </button>
                            {!isSubAdmin && <button
                                onClick={() => { setSelected(detailsModal); setDetailsModal(null); setOpen(true); }}
                                className="px-3 py-1.5 bg-amber-500 hover:bg-amber-600 text-white rounded-md text-xs font-medium transition flex items-center gap-1.5"
                            >
                                <FiEdit2 size={12} /> Edit Contractor
                            </button>}
                        </div>
                    </div>
                </div>
            )}

            {/* delete modal */}
            <DeleteModal
                isOpen={!!deleteTarget}
                onClose={() => setDeleteTarget(null)}
                onConfirm={handleDeleteConfirm}
                isDeleting={deleting}
                title="Delete Contractor"
                message={`Are you sure you want to delete ${deleteTarget?.name}? This action cannot be undone.`}
            />

            {/* add / edit modal */}
            <ContractorModal
                isOpen={open}
                onSuccess={fetchInitialData}
                onClose={() => setOpen(false)}
                eventId={eventId}
                contractor={selected}
            />
        </div>
    );
};

export default ContractorsTab;